import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  clientApi,
  RetailerCommonMistake,
  RetailerLabelType,
  RetailerManifest,
  RetailerTemplateDimensions,
  RetailerTemplateSummary,
} from '../../api/client';
import { EmptyState } from '../../components/EmptyState';
import { SkeletonTable } from '../../components/SkeletonTable';

const LABEL_TYPE_BADGE_STYLES: Record<RetailerLabelType, string> = {
  shipping: 'bg-blue-100 text-blue-800',
  pallet: 'bg-amber-100 text-amber-800',
  carton: 'bg-emerald-100 text-emerald-800',
  box: 'bg-violet-100 text-violet-800',
  innerpack: 'bg-cyan-100 text-cyan-800',
  placard: 'bg-rose-100 text-rose-800',
  custom: 'bg-slate-100 text-slate-800',
};

const MISTAKE_SEVERITY_STYLES: Record<RetailerCommonMistake['severity'], string> = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-blue-100 text-blue-800',
};

export function RetailerDetailPage() {
  const { retailerId } = useParams<{ retailerId: string }>();

  const { data: retailer, isLoading, error } = useQuery({
    queryKey: ['client', 'retailer', retailerId],
    queryFn: () => clientApi.getRetailerManifest(retailerId!),
    enabled: !!retailerId,
  });

  if (isLoading) {
    return <RetailerDetailSkeleton />;
  }

  if (error || !retailer) {
    return (
      <div className="space-y-4">
        <Link to="/retailers" className="text-sm font-medium text-primary-600 hover:text-primary-800">
          ← Back to Retailers
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <h1 className="text-lg font-semibold text-red-900">Retailer not found</h1>
          <p className="mt-1 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Could not load retailer details.'}
          </p>
        </div>
      </div>
    );
  }

  const templates = getTemplatesForDisplay(retailer);

  return (
    <div className="space-y-6">
      <Link to="/retailers" className="text-sm font-medium text-primary-600 hover:text-primary-800">
        ← Back to Retailers
      </Link>

      <div className="card">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{retailer.displayName}</h1>
            <p className="mt-1 font-mono text-sm text-gray-500">{retailer.retailerId}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {retailer.labelTypes.map((labelType) => (
                <span
                  key={labelType}
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${LABEL_TYPE_BADGE_STYLES[labelType]}`}
                >
                  {formatSlug(labelType)}
                </span>
              ))}
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div className="rounded-lg bg-gray-50 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last reviewed</dt>
              <dd className="mt-1 font-medium text-gray-900">{formatDate(retailer.lastReviewedAt)}</dd>
            </div>
            <div className="rounded-lg bg-gray-50 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Content version</dt>
              <dd className="mt-1 font-medium text-gray-900">{retailer.contentVersion}</dd>
            </div>
            <div className="rounded-lg bg-gray-50 px-4 py-3 sm:col-span-2">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Templates</dt>
              <dd className="mt-1 font-medium text-gray-900">
                {templates.length} active template{templates.length === 1 ? '' : 's'}
              </dd>
            </div>
          </dl>
        </div>

        {retailer.notes && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            {retailer.notes}
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Supported Programs</h2>
          <span className="text-sm text-gray-500">{retailer.supportedPrograms.length} total</span>
        </div>
        {retailer.supportedPrograms.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {retailer.supportedPrograms.map((program) => (
              <span
                key={program}
                className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
              >
                {formatSlug(program)}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm text-gray-500">No supported programs documented.</p>
        )}
      </div>

      <div className="card overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Templates</h2>
          <span className="text-sm text-gray-500">{templates.length} total</span>
        </div>

        {templates.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon="📄"
              title="No templates configured"
              description="This retailer does not have any active templates yet."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    Template Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    Label Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    Dimensions
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {templates.map((template) => (
                  <tr key={template.templateKey} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-mono text-sm text-gray-900">{template.templateKey}</div>
                      {template.displayName && (
                        <div className="mt-1 text-sm text-gray-500">{template.displayName}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {template.labelType ? formatSlug(template.labelType) : 'Not documented'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {formatDimensions(template.dimensions)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/retailers/${retailer.retailerId}/templates/${encodeURIComponent(template.templateKey)}`}
                        className="text-sm font-medium text-primary-600 hover:text-primary-800"
                      >
                        View template →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {retailer.commonMistakes && retailer.commonMistakes.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900">Common Mistakes</h2>
          <div className="mt-4 space-y-4">
            {retailer.commonMistakes.map((mistake, index) => (
              <div key={`${mistake.mistake}-${index}`} className="rounded-lg border border-gray-200 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{mistake.mistake}</h3>
                    <p className="mt-2 text-sm text-gray-600">{mistake.correction}</p>
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${MISTAKE_SEVERITY_STYLES[mistake.severity]}`}
                  >
                    {mistake.severity.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RetailerDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
      <div className="card animate-pulse space-y-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="h-8 w-52 rounded bg-gray-200" />
            <div className="h-4 w-32 rounded bg-gray-100" />
            <div className="flex flex-wrap gap-2 pt-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-6 w-20 rounded-full bg-gray-100" />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-20 w-44 rounded-lg bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
      <div className="card animate-pulse">
        <div className="h-6 w-40 rounded bg-gray-200" />
        <div className="mt-4 flex flex-wrap gap-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="h-6 w-28 rounded-full bg-gray-100" />
          ))}
        </div>
      </div>
      <div className="card p-0">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="h-6 w-32 animate-pulse rounded bg-gray-200" />
        </div>
        <SkeletonTable rows={6} columns={4} />
      </div>
    </div>
  );
}

function getTemplatesForDisplay(retailer: RetailerManifest): RetailerTemplateSummary[] {
  if (retailer.templates && retailer.templates.length > 0) {
    return retailer.templates;
  }

  return retailer.activeTemplates.map((templateKey) => ({
    templateKey,
    labelType: retailer.labelTypes.find((labelType) => templateKey.includes(`--${labelType}-`)),
  }));
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function formatDimensions(dimensions?: RetailerTemplateDimensions): string {
  if (!dimensions) {
    return 'Not documented';
  }

  return `${dimensions.width} x ${dimensions.height} ${dimensions.unit} @ ${dimensions.dpi} DPI`;
}

function formatSlug(value: string): string {
  return value
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}
